/**
 * BFF contract RUNTIME schemas — the wire-validation twin of `bff-contract.ts`.
 *
 * `BffContract` is (and stays) a TYPE-ONLY map, so nothing existed at runtime to
 * parse the wire against: `bff()` cast `res.json()` to the contract response and
 * `bffPost` cast the request body to the contract request. Both casts asserted
 * the contract without CHECKING it — a request hand-crafted off-contract (curl,
 * an attacker, a stale client) or a drifted server response flowed straight into
 * typed code (R5 W6 PR-4 closes this).
 *
 * This module defines one zod (`zod/mini`) schema per contract endpoint and per
 * direction, and the seam PARSES instead of casting:
 *   - `bff-route.ts` (`bffPost`) parses every request body → invalid ⇒ 400
 *     `{ success: false, error }` envelope,
 *   - `bff.ts` (`bff`/`bffGet`) parses every response → unexpected shape ⇒ a
 *     structured error, never a silent cast-through.
 *
 * DRIFT IS A COMPILE ERROR — the schemas are proven IDENTICAL to the contract:
 *   1. the maps are `satisfies`-keyed by `BffEndpoint`/`BffGetEndpoint` (a
 *      missing or extra endpoint is a type error), and
 *   2. the `BffPostSchemaParityGate`/`BffGetSchemaParityGate` gates at the bottom `Exact<>`-compares every
 *      schema's inferred output with the contract type, in BOTH directions and
 *      including optionality/readonly modifiers. Change a contract type (or any
 *      view-model it references) without the matching schema edit and the
 *      offending endpoint literal appears in a type error here.
 * So the schema can never silently drift from the SSOT: `BffContract` stays the
 * single authoritative shape, and this file is its compiler-verified runtime
 * projection.
 *
 * Enum vocabularies are DERIVED, not hand-listed: proto enum NAME unions
 * (`keyof typeof Enum`) come from the generated enum objects via
 * {@link protoEnumNameSchema}, wire-value unions from the generated
 * `PROTO_ENUM_VALUE_SET_<Enum>`, locales from the generated `LOCALES` — adding a
 * variant flows through `bazel run //build/sync:sync` into both the type AND
 * the runtime check.
 *
 * `zod/mini` (not full `zod`): this module is client-bundled (`bff()` parses
 * responses in the browser), and the functional mini API tree-shakes to a few
 * kB instead of pulling the full chainable runtime into the public storefront.
 */
import type {
  BffContract,
  BffEndpoint,
  BffGetContract,
  BffGetEndpoint,
} from '@/shared/lib/bff-contract';
import { PROTO_ENUM_VALUE_SET_SupportCaseCategory } from '@letsgethome/lib/proto-enums/value_sets_gen';
import type { ServerErrorInfo } from '@letsgethome/lib/server-errors';
import { FloorLevel } from '@letsgethome/proto/common/v1/floor_level_pb';
import { PetsPolicy } from '@letsgethome/proto/common/v1/pets_policy_pb';
import { PropertyType } from '@letsgethome/proto/common/v1/property_type_pb';
import { PaymentStatus } from '@letsgethome/proto/payments/v1/payments_pb';
import {
  type BuildingId,
  type ConversationId,
  type DistrictId,
  type EstateId,
  type ImageId,
  type ListingId,
  type MessageId,
  type SaveableId,
  type Slug,
} from '@letsgethome/types/branded';
import { isDistrictSlug, type DistrictSlug } from '@letsgethome/types/districts';
import * as z from 'zod/mini';

import { brandedString, exactIntSchema, protoEnumNameSchema } from './bff-schema-helpers';
// …and IMPORTED as well, because a re-export does not bind the name in this
// module's own scope: the `SchemaParityExact` obligation below names it in
// `z.infer<typeof serverErrorInfoSchema>`, and a re-export alone cannot
// satisfy that. `import type` suffices — the only use is in type position.
import {
  conversationDataSchema,
  e164PhoneSchema,
  estateResolutionDataSchema,
  filtersMetadataDataSchema,
  inquiryLeaseTermSchema,
  inquiryMessageSchema,
  inquiryOccupantsSchema,
  inquirySaleTimeframeSchema,
  intentTypeSchema,
  isoDateStringSchema,
  listingDescriptionSchema,
  listingDraftDataSchema,
  listingImageDataSchema,
  localeSchema,
  locationSuggestionDataSchema,
  messageDataSchema,
  otpCodeSchema,
  profileNameSchema,
  realtimeTokensSchema,
  searchListingsResultSchema,
  searchSuggestionsDataSchema,
  SUPPORT_MESSAGE_MAX_LENGTH,
  transactionModeSchema,
  userProfileSchema,
  type serverErrorInfoSchema,
} from './bff-schema-view-models';

/**
 * Re-exported so this module's PUBLIC surface is unchanged by the size split:
 * `serverErrorInfoSchema` was exported from here before the view-model schemas
 * moved to their own file, and `bff.ts` imports it from this door.
 */
export { serverErrorInfoSchema } from './bff-schema-view-models';

// ---------------------------------------------------------------------------
// Request-body schemas
// ---------------------------------------------------------------------------

/** `ManualAddressWrite` — the ADDRESS step's structured manual address (D5). */
const manualAddressWriteSchema = z.object({
  districtId: z.optional(brandedString<DistrictId>()),
  estateName: z.string(),
  phaseName: z.optional(z.string()),
  blockName: z.optional(z.string()),
  streetAddress: z.optional(z.string()),
});

/** `UpdateListingStepBody` — discriminated on `step`, one arm per wizard step. */
const updateListingStepBodySchema = z.discriminatedUnion('step', [
  z.object({
    listingId: brandedString<ListingId>(),
    step: z.literal('ADDRESS'),
    buildingId: z.optional(brandedString<BuildingId>()),
    manual: z.optional(manualAddressWriteSchema),
    floorLevel: z.optional(protoEnumNameSchema(FloorLevel)),
    floor: z.optional(z.string()),
    unit: z.optional(z.string()),
  }),
  z.object({
    listingId: brandedString<ListingId>(),
    step: z.literal('SIZE'),
    saleableArea: z.optional(z.number()),
    propertyType: z.optional(protoEnumNameSchema(PropertyType)),
    bedrooms: z.optional(z.number()),
    bathrooms: z.optional(z.number()),
    petsPolicy: z.optional(protoEnumNameSchema(PetsPolicy)),
  }),
  z.object({
    listingId: brandedString<ListingId>(),
    step: z.literal('PRICING'),
    forSale: z.optional(z.boolean()),
    forRent: z.optional(z.boolean()),
    salePriceCents: z.optional(z.number()),
    rentPriceCents: z.optional(z.number()),
  }),
  z.object({
    listingId: brandedString<ListingId>(),
    step: z.literal('AMENITIES'),
    // The zod twin of the contract's `amenities` (LET-99). REQUIRED, and a
    // message rather than a bare list, for the reason the proto gives: an
    // absent message means "this request does not speak about amenities" and
    // a present-and-empty one means "the owner selected none". Only the second
    // is reachable through this step, and it CLEARS.
    amenities: z.object({ slugs: z.array(brandedString<Slug>()) }),
  }),
  z.object({
    listingId: brandedString<ListingId>(),
    step: z.literal('DESCRIPTION'),
    description: z.optional(listingDescriptionSchema),
    // Zod twin of the contract's `descriptionLocale` (R4): the authoring
    // locale the wizard sends so the server files the draft under the right
    // origin instead of guessing it.
    descriptionLocale: z.optional(localeSchema),
  }),
]);

/** `SearchListingsParams & { locale }` — the `/api/search/listings` body. */
const searchListingsBodySchema = z.object({
  mode: transactionModeSchema,
  page: z.optional(z.number()),
  perPage: z.optional(z.number()),
  filters: z.optional(
    z.object({
      bounds: z.optional(
        z.object({
          north: z.number(),
          east: z.number(),
          south: z.number(),
          west: z.number(),
        })
      ),
      districtId: z.optional(brandedString<DistrictId>()),
      // The official-district facet scope. Validated against the CLOSED
      // committed vocabulary rather than "is a string": an off-vocabulary slug
      // is decidable here, at the door, exactly as it is in the facet loader.
      districtSlug: z.optional(z.custom<DistrictSlug>(isDistrictSlug)),
      estateId: z.optional(brandedString<EstateId>()),
      // R4-2: the decimal-digit carrier, on BOTH search endpoints. A number
      // is REFUSED rather than coerced — the contract type is `ExactInt`, and
      // the parity gate below is what licenses the schema to be that exact.
      minPriceCents: z.optional(exactIntSchema()),
      maxPriceCents: z.optional(exactIntSchema()),
      bedrooms: z.optional(z.number()),
      bathrooms: z.optional(z.number()),
      petsAllowed: z.optional(z.boolean()),
      propertyType: z.optional(protoEnumNameSchema(PropertyType)),
      query: z.optional(z.string()),
    })
  ),
  locale: localeSchema,
});

/** A `{ listingId }` body (publish/delete/unlist/relist/delete-image parents). */
const listingIdBodySchema = z.object({ listingId: brandedString<ListingId>() });

// ---------------------------------------------------------------------------
// The per-endpoint schema maps (runtime twin of `BffContract`)
// ---------------------------------------------------------------------------

/**
 * POST schemas keyed EXACTLY by `BffEndpoint` (`satisfies` — a missing or extra
 * endpoint is a compile error) and proven shape-identical to the contract by
 * the `BffPostSchemaParityGate`/`BffGetSchemaParityGate` gates below.
 */
/** `NotificationPrefsData` (`account.v1.NotificationPrefs` view-model). */
const notificationPrefsDataSchema = z.object({
  emailEnabled: z.boolean(),
  smsEnabled: z.boolean(),
  whatsappEnabled: z.boolean(),
  pushEnabled: z.boolean(),
});

export const BFF_POST_SCHEMAS = {
  '/api/account/update-profile': {
    // PATCH semantics (PR-J-c) — each field optional; only what's present
    // is updated (the proto request is `optional` per field).
    request: z.object({
      givenName: z.optional(profileNameSchema),
      familyName: z.optional(profileNameSchema),
      autoTranslate: z.optional(z.boolean()),
    }),
    response: userProfileSchema,
  },
  '/api/account/update-notification-prefs': {
    request: z.object({
      emailEnabled: z.optional(z.boolean()),
      smsEnabled: z.optional(z.boolean()),
      whatsappEnabled: z.optional(z.boolean()),
      pushEnabled: z.optional(z.boolean()),
    }),
    response: notificationPrefsDataSchema,
  },
  '/api/auth/otp/send': {
    request: z.object({ phoneNumber: e164PhoneSchema }),
    response: z.undefined(),
  },
  '/api/auth/otp/verify': {
    request: z.object({ phoneNumber: e164PhoneSchema, code: otpCodeSchema }),
    response: z.optional(userProfileSchema),
  },
  '/api/auth/google': {
    request: z.object({ code: z.string() }),
    response: z.optional(userProfileSchema),
  },
  '/api/auth/logout': {
    request: z.undefined(),
    response: z.undefined(),
  },
  '/api/conversations/send-message': {
    request: z.object({ conversationId: brandedString<ConversationId>(), content: z.string() }),
    response: messageDataSchema,
  },
  '/api/conversations/mark-read': {
    request: z.object({
      conversationId: brandedString<ConversationId>(),
      lastReadMessageId: brandedString<MessageId>(),
    }),
    response: z.undefined(),
  },
  '/api/support/ensure-case': {
    // Runtime twin of the PR-H1 support contract: category from the
    // generated slug set (UNSPECIFIED stripped by construction); the
    // optional first message mirrors the wire ceiling
    // (`EnsureSupportCaseRequest.initial_message` max_len via
    // protoboundsgen).
    request: z.object({
      category: z.enum(PROTO_ENUM_VALUE_SET_SupportCaseCategory),
      message: z.optional(z.string().check(z.maxLength(SUPPORT_MESSAGE_MAX_LENGTH))),
    }),
    response: z.object({
      conversationId: brandedString<ConversationId>(),
      created: z.boolean(),
    }),
  },
  '/api/realtime/token': {
    request: z.object({ channel: z.optional(z.string()) }),
    response: realtimeTokensSchema,
  },
  '/api/feedbacks/submit': {
    request: z.object({ message: z.string() }),
    response: z.undefined(),
  },
  '/api/leads/start-inquiry': {
    // The FULL submission (PR-E1) — runtime twin of `StartInquiryBody`.
    // Optionals are the per-intent qualifying fields; the form only sends the
    // ones in the wasm inquiry matrix for the chosen intent, and the numeric
    // bounds mirror the proto `buf.validate` rules (protoboundsgen).
    request: z.object({
      listingId: brandedString<ListingId>(),
      intentType: intentTypeSchema,
      message: inquiryMessageSchema,
      moveInDate: z.optional(isoDateStringSchema),
      occupantsCount: z.optional(inquiryOccupantsSchema),
      saleMaxPriceCents: z.optional(z.int().check(z.gte(1))),
      saleTimeframeMonths: z.optional(inquirySaleTimeframeSchema),
      // F-1: rent-side qualifying pair (rent inquiries). Same JSON-safe
      // plain-cents shape as `saleMaxPriceCents`; `leaseTermMonths` mirrors the
      // wire `gt: 0, lte: 600`.
      rentMaxPriceCents: z.optional(z.int().check(z.gte(1))),
      leaseTermMonths: z.optional(inquiryLeaseTermSchema),
    }),
    response: z.object({ conversationId: brandedString<ConversationId>() }),
  },
  '/api/listings/create': {
    request: z.object({ id: brandedString<ListingId>() }),
    response: z.object({ id: brandedString<ListingId>() }),
  },
  '/api/listings/update-step': {
    request: updateListingStepBodySchema,
    response: listingDraftDataSchema,
  },
  '/api/payments/create-checkout-session': {
    request: z.object({ listingId: brandedString<ListingId>(), locale: localeSchema }),
    // The browser `window.location.assign`s this verbatim — parse it as an
    // https URL, never an arbitrary string (a mangled/`javascript:` value
    // fails the schema instead of navigating).
    response: z.object({ checkoutUrl: z.url({ protocol: /^https$/ }) }),
  },
  '/api/payments/status': {
    request: z.object({ listingId: brandedString<ListingId>() }),
    response: z.object({ status: protoEnumNameSchema(PaymentStatus) }),
  },
  '/api/listings/publish': {
    request: listingIdBodySchema,
    response: listingDraftDataSchema,
  },
  '/api/listings/save-description': {
    request: z.object({
      listingId: brandedString<ListingId>(),
      description: z.optional(z.string()),
      descriptionLocale: z.optional(localeSchema),
      publish: z.boolean(),
    }),
    response: listingDraftDataSchema,
  },
  '/api/listings/delete': {
    request: listingIdBodySchema,
    response: z.undefined(),
  },
  '/api/listings/unlist': {
    request: listingIdBodySchema,
    response: listingDraftDataSchema,
  },
  '/api/listings/relist': {
    request: listingIdBodySchema,
    response: listingDraftDataSchema,
  },
  '/api/listings/retry-translation': {
    request: listingIdBodySchema,
    response: listingDraftDataSchema,
  },
  '/api/listings/presigned-url': {
    request: z.object({
      filename: z.string(),
      contentType: z.string(),
      contentLength: z.number(),
    }),
    response: z.object({ uploadUrl: z.string(), blobKey: z.string() }),
  },
  '/api/listings/add-image': {
    request: z.object({
      id: brandedString<ImageId>(),
      listingId: brandedString<ListingId>(),
      blobKey: z.string(),
      filename: z.string(),
      contentType: z.string(),
      byteSize: z.number(),
    }),
    response: z.object({ image: listingImageDataSchema, listing: listingDraftDataSchema }),
  },
  '/api/listings/delete-image': {
    request: z.object({ listingId: brandedString<ListingId>(), imageId: brandedString<ImageId>() }),
    response: listingDraftDataSchema,
  },
  '/api/listings/reorder-images': {
    request: z.object({
      listingId: brandedString<ListingId>(),
      imageIds: z.array(brandedString<ImageId>()),
    }),
    response: listingDraftDataSchema,
  },
  '/api/saved/create': {
    request: z.object({
      saveableId: brandedString<SaveableId>(),
      saveableType: z.enum(['listing', 'estate']),
    }),
    response: z.undefined(),
  },
  '/api/saved/delete': {
    request: z.object({
      saveableId: brandedString<SaveableId>(),
      saveableType: z.enum(['listing', 'estate']),
    }),
    response: z.undefined(),
  },
  '/api/search/suggestions': {
    request: z.object({ query: z.string(), limit: z.optional(z.number()) }),
    response: searchSuggestionsDataSchema,
  },
  '/api/search/listings': {
    request: searchListingsBodySchema,
    response: searchListingsResultSchema,
  },
  '/api/search/filters-metadata': {
    request: z.object({
      mode: transactionModeSchema,
      estateId: z.optional(brandedString<EstateId>()),
      districtId: z.optional(brandedString<DistrictId>()),
      // R4-2: the SAME helper as the listings request above — the hand copy
      // (a bare `z.string()` beside a `.toString()` caller) was the class.
      minPriceCents: z.optional(exactIntSchema()),
      maxPriceCents: z.optional(exactIntSchema()),
      minBedrooms: z.optional(z.number()),
      minBathrooms: z.optional(z.number()),
      // U-4: the refinements the metadata request could not express, so the
      // previewed count matches the search it previews.
      petsAllowed: z.optional(z.boolean()),
      propertyType: z.optional(protoEnumNameSchema(PropertyType)),
      query: z.optional(z.string()),
    }),
    response: filtersMetadataDataSchema,
  },
  '/api/wizard/suggest-locations': {
    request: z.object({ query: z.string(), limit: z.optional(z.number()) }),
    response: z.array(locationSuggestionDataSchema),
  },
  '/api/wizard/resolve-estate': {
    request: z.object({ estateId: brandedString<EstateId>() }),
    response: estateResolutionDataSchema,
  },
} satisfies Record<BffEndpoint, { request: z.ZodMiniType; response: z.ZodMiniType }>;

/** GET schemas keyed exactly by `BffGetEndpoint` (responses only — no body). */
export const BFF_GET_SCHEMAS = {
  // W3.2: discriminated on `isAuthenticated` — the runtime twin of the
  // contract union, so an "authenticated with nobody" payload fails the
  // seam parse instead of leaking into client state.
  '/api/auth/me': z.union([
    z.object({
      isAuthenticated: z.literal(false),
      user: z.null(),
    }),
    z.object({
      isAuthenticated: z.literal(true),
      user: userProfileSchema,
    }),
  ]),
  '/api/conversations/unread-count': z.object({
    unreadConversationsCount: z.number(),
  }),
  // W-6: the sidebar's conversation list. Empty for a signed-out session, and
  // for an RPC failure — the sidebar degrades to "no conversations", it never
  // throws at the seam (the badge beside it has the same best-effort contract).
  '/api/conversations/list': z.object({
    conversations: z.array(conversationDataSchema),
  }),
} satisfies Record<BffGetEndpoint, z.ZodMiniType>;

// ---------------------------------------------------------------------------
// Typed accessors — the only doors the seam uses
// ---------------------------------------------------------------------------

/**
 * The request-body schema for a POST endpoint, typed as parsing to the
 * CONTRACT request type. Sound by the `BffPostSchemaParityGate`/`BffGetSchemaParityGate` gates: the schema's
 * output is compiler-proven identical to `BffContract[E]['request']`.
 */
export function bffRequestSchema<E extends BffEndpoint>(
  endpoint: E
): z.ZodMiniType<BffContract[E]['request']> {
  return BFF_POST_SCHEMAS[endpoint].request as z.ZodMiniType<BffContract[E]['request']>;
}

/** The response (`data`) schema for a POST endpoint — parity-gated like above. */
export function bffResponseSchema<E extends BffEndpoint>(
  endpoint: E
): z.ZodMiniType<BffContract[E]['response']> {
  return BFF_POST_SCHEMAS[endpoint].response as z.ZodMiniType<BffContract[E]['response']>;
}

/**
 * The full response-body schema for a GET endpoint.
 *
 * Returns the schema's OWN type — the indexed access into `BFF_GET_SCHEMAS` —
 * rather than re-asserting it as `z.ZodMiniType<BffGetContract[E]>`. That
 * removes the `as unknown as` double cast entirely, and it is strictly more
 * precise: callers see the actual schema, and `.safeParse(...).data` lands on
 * `BffGetContract[E]` structurally because `BffGetSchemaParityGate` below
 * `Exact<>`-proves the two identical.
 *
 * Deliberately NOT fixed by annotating `BFF_GET_SCHEMAS` as
 * `{ [E in BffGetEndpoint]: z.ZodMiniType<BffGetContract[E]> }`: that would
 * make `z.infer<(typeof BFF_GET_SCHEMAS)[E]>` equal `BffGetContract[E]` BY
 * DEFINITION, so the parity gate would compare a type against itself and pass
 * vacuously — the drift it exists to catch would stop being caught.
 */
export function bffGetResponseSchema<E extends BffGetEndpoint>(
  endpoint: E
): (typeof BFF_GET_SCHEMAS)[E] {
  return BFF_GET_SCHEMAS[endpoint];
}

/** One-line human summary of a parse failure (issue messages + paths, no values). */
export function summarizeSchemaError(error: z.core.$ZodError): string {
  return error.issues
    .map((issue) =>
      issue.path.length > 0 ? `${issue.path.join('.')}: ${issue.message}` : issue.message
    )
    .join('; ');
}

// ---------------------------------------------------------------------------
// The parity gate — schemas ⟺ contract, enforced by the compiler
// ---------------------------------------------------------------------------

/**
 * JSON-shaped primitives pass through untouched (this keeps branded
 * string/number/bigint refinements intact — they extend their base primitive).
 */
type SchemaParityPrimitive = string | number | boolean | bigint | symbol | null | undefined;

/**
 * Structural normalization applied to BOTH sides before the identity compare:
 * flattens intersections (`Linkable & {…}`, `SearchListingsParams & {…}`) and
 * interfaces into plain object shapes — TS's conditional-type identity check
 * would otherwise report a false mismatch on representation, not shape. The
 * mapped type is homomorphic, so optionality (`?`) and `readonly` modifiers are
 * PRESERVED and still compared strictly.
 */
type SchemaParityNormalize<T> = T extends SchemaParityPrimitive
  ? T
  : T extends (infer U)[]
    ? SchemaParityNormalize<U>[]
    : T extends readonly (infer U)[]
      ? readonly SchemaParityNormalize<U>[]
      : T extends object
        ? { [K in keyof T]: SchemaParityNormalize<T[K]> }
        : T;

/**
 * TRUE iff `A` and `B` are structurally IDENTICAL (both-direction, exact —
 * catches missing/extra fields, optionality, readonly, and brand mismatches;
 * plain mutual assignability would miss dropped optional fields).
 */
/* eslint-disable @typescript-eslint/no-unnecessary-type-parameters -- the
   `<T>() => …` generic-signature pair IS the exactness check: TS compares two
   generic function signatures for structural IDENTITY (not mere mutual
   assignability), which is what catches optionality/readonly/brand drift. The
   "unused" T is load-bearing. */
type SchemaParityExact<A, B> =
  (<T>() => T extends SchemaParityNormalize<A> ? 1 : 2) extends <
    T,
  >() => T extends SchemaParityNormalize<B> ? 1 : 2
    ? true
    : false;
/* eslint-enable @typescript-eslint/no-unnecessary-type-parameters */

/**
 * The POST endpoints whose schema does NOT match the contract — `never` when
 * everything is in parity. A drifted endpoint's literal shows up verbatim in
 * the `BffPostSchemaParityGate` error below.
 */
type MismatchedPostEndpoints = {
  [E in BffEndpoint]: [
    SchemaParityExact<z.infer<(typeof BFF_POST_SCHEMAS)[E]['request']>, BffContract[E]['request']>,
    SchemaParityExact<
      z.infer<(typeof BFF_POST_SCHEMAS)[E]['response']>,
      BffContract[E]['response']
    >,
  ] extends [true, true]
    ? never
    : E;
}[BffEndpoint];

/** The GET endpoints whose schema does not match the contract. */
type MismatchedGetEndpoints = {
  [E in BffGetEndpoint]: SchemaParityExact<
    z.infer<(typeof BFF_GET_SCHEMAS)[E]>,
    BffGetContract[E]
  > extends true
    ? never
    : E;
}[BffGetEndpoint];

/**
 * COMPILE-TIME GATE: if any schema drifts from `BffContract`/`BffGetContract`
 * (or from `ServerErrorInfo`), the drifted endpoint literal fails the `never`
 * constraint here — e.g. `Type '"/api/wizard/resolve-estate"' does not satisfy 'never'`.
 * This is what makes the runtime schemas "identical with the type SSOT" a
 * checked invariant instead of a convention.
 */
type StaticAssertNoDrift<Drifted extends never> = Drifted;
export type BffPostSchemaParityGate = StaticAssertNoDrift<MismatchedPostEndpoints>;
export type BffGetSchemaParityGate = StaticAssertNoDrift<MismatchedGetEndpoints>;
export type BffErrorInfoParityGate = StaticAssertNoDrift<
  SchemaParityExact<z.infer<typeof serverErrorInfoSchema>, ServerErrorInfo> extends true
    ? never
    : 'ServerErrorInfo'
>;
